import { create } from 'zustand';
import { authApi } from '@/api/client';
import { safeStorage } from '@/utils';
import type { User, Token } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: !!safeStorage.getItem('access_token'),
  isLoading: false,
  error: null,

  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login(username, password);
      const token: Token = response.data;
      safeStorage.setItem('access_token', token.access_token);
      safeStorage.setItem('refresh_token', token.refresh_token || '');
      set({ isAuthenticated: true, isLoading: false });
      await get().fetchUser();
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Login failed';
      set({ error: message, isLoading: false, isAuthenticated: false });
      throw new Error(message);
    }
  },

  register: async (username: string, email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      await authApi.register({ username, email, password });
      await get().login(username, password);
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Registration failed';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  logout: () => {
    safeStorage.removeItem('access_token');
    safeStorage.removeItem('refresh_token');
    set({ user: null, isAuthenticated: false, error: null });
  },

  fetchUser: async () => {
    try {
      const response = await authApi.me();
      set({ user: response.data, isAuthenticated: true });
    } catch {
      set({ user: null, isAuthenticated: false });
      safeStorage.removeItem('access_token');
      safeStorage.removeItem('refresh_token');
    }
  },

  clearError: () => set({ error: null }),
}));
